<script>
import { mapGetters } from 'vuex';
import { useAutomation } from 'dashboard/composables/useAutomation';
import { useEditableAutomation } from 'dashboard/composables/useEditableAutomation';
import FilterInputBox from 'dashboard/components/widgets/FilterInput/Index.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AutomationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import BranchesEditor from './components/BranchesEditor.vue';
import {
  getFileName,
  generateAutomationPayload,
  getAttributes,
  getInputType,
  getOperators,
  getCustomAttributeType,
  showActionInput,
  getDefaultConditions,
  getDefaultActions,
} from 'dashboard/helper/automationHelper';
import { validateAutomation } from 'dashboard/helper/validations';

import { AUTOMATION_RULE_EVENTS, AUTOMATION_ACTION_TYPES } from './constants';

export default {
  components: {
    FilterInputBox,
    NextButton,
    AutomationActionInput,
    BranchesEditor,
  },
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    selectedResponse: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['saveAutomation'],
  setup() {
    const {
      automation,
      automationTypes,
      onEventChange,
      getConditionDropdownValues,
      appendNewCondition,
      appendNewAction,
      removeFilter,
      removeAction,
      resetFilter,
      resetAction,
      getActionDropdownValues,
      manifestCustomAttributes,
    } = useAutomation();
    const { formatAutomation } = useEditableAutomation();
    return {
      automation,
      automationTypes,
      onEventChange,
      getConditionDropdownValues,
      appendNewCondition,
      appendNewAction,
      removeFilter,
      removeAction,
      resetFilter,
      resetAction,
      getActionDropdownValues,
      formatAutomation,
      manifestCustomAttributes,
    };
  },
  data() {
    return {
      automationRuleEvent: AUTOMATION_RULE_EVENTS[0].key,
      automationMutated: false,
      show: true,
      showDeleteConfirmationModal: false,
      allCustomAttributes: [],
      mode: 'edit',
      errors: {},
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    automationRuleEvents() {
      return AUTOMATION_RULE_EVENTS.map(event => ({
        ...event,
        value: this.$t(`AUTOMATION.EVENTS.${event.value}`),
      }));
    },
    hasAutomationMutated() {
      if (
        this.automation.conditions[0].values ||
        this.automation.actions[0].action_params.length
      )
        return true;
      return false;
    },
    automationActionTypes() {
      const actionTypes = this.isFeatureEnabled('sla')
        ? AUTOMATION_ACTION_TYPES
        : AUTOMATION_ACTION_TYPES.filter(({ key }) => key !== 'add_sla');

      return actionTypes.map(action => ({
        ...action,
        label: this.$t(`AUTOMATION.ACTIONS.${action.label}`),
      }));
    },
  },
  watch: {
    'automation.event_name'(newEventName, oldEventName) {
      // Solo reinicializar si realmente cambió el evento Y no estamos en la carga inicial
      if (newEventName !== oldEventName && oldEventName) {
        // Cuando cambia el evento, reinicializar conditions en modo simple
        if (this.automation.execution_mode === 'simple') {
          // Reiniciar conditions y actions con los defaults del nuevo evento
          this.automation.conditions = this.getDefaultConditions(newEventName);
          this.automation.actions = this.getDefaultActions();
        }
        // En modo branched, actualizar las condiciones de todas las ramas
        if (
          this.automation.execution_mode === 'branched' &&
          this.automation.branches
        ) {
          const defaultCondition = this.getDefaultConditions(newEventName);
          const defaultAction = this.getDefaultActions();

          // Función recursiva para actualizar condiciones en todas las ramas
          const updateBranchConditions = branches => {
            return branches.map(branch => ({
              ...branch,
              conditions: defaultCondition,
              actions: defaultAction,
              branches: branch.branches
                ? updateBranchConditions(branch.branches)
                : [],
            }));
          };

          this.automation.branches = updateBranchConditions(
            this.automation.branches
          );
        }
      }
    },
    'automation.execution_mode'(newMode, oldMode) {
      if (newMode !== oldMode) {
        // Cuando se cambia de simple a branched, inicializar branches si no existen
        if (newMode === 'branched') {
          if (
            !this.automation.branches ||
            !Array.isArray(this.automation.branches)
          ) {
            this.automation.branches = [];
          }
        }
        // Cuando se cambia de branched a simple, asegurar que conditions y actions existan
        if (newMode === 'simple') {
          if (
            !this.automation.conditions ||
            !Array.isArray(this.automation.conditions)
          ) {
            this.appendNewCondition();
          }
          if (
            !this.automation.actions ||
            !Array.isArray(this.automation.actions)
          ) {
            this.appendNewAction();
          }
        }
      }
    },
  },
  mounted() {
    this.manifestCustomAttributes();
    this.allCustomAttributes = this.$store.getters['attributes/getAttributes'];

    this.automation = this.formatAutomation(
      this.selectedResponse,
      this.allCustomAttributes,
      this.automationTypes,
      this.automationActionTypes
    );
  },
  methods: {
    getFileName,
    getAttributes,
    getInputType,
    getOperators,
    getCustomAttributeType,
    showActionInput,
    getDefaultConditions,
    getDefaultActions,
    isFeatureEnabled(flag) {
      return this.isFeatureEnabledonAccount(this.accountId, flag);
    },
    emitSaveAutomation() {
      if (this.automation.execution_mode === 'branched') {
        this.errors = {};
        if (!this.automation.name) {
          this.errors.name = true;
        }
        if (
          !this.automation.branches ||
          this.automation.branches.length === 0
        ) {
          this.$root.$buefy.toast.open({
            message: this.$t('AUTOMATION.BRANCHES.NO_BRANCHES_ERROR'),
            type: 'is-danger',
          });
          return;
        }
      } else {
        this.errors = validateAutomation(this.automation);
      }
      if (Object.keys(this.errors).length === 0) {
        const automation = generateAutomationPayload(this.automation);
        this.$emit('saveAutomation', automation, this.mode);
      }
    },
    getTranslatedAttributes(type, event) {
      return getAttributes(type, event).map(attribute => {
        // Skip translation
        // 1. If customAttributeType key is present then its rendering attributes from API
        // 2. If contact_custom_attribute or conversation_custom_attribute is present then its rendering section title
        const skipTranslation =
          attribute.customAttributeType ||
          [
            'contact_custom_attribute',
            'conversation_custom_attribute',
          ].includes(attribute.key);

        return {
          ...attribute,
          name: skipTranslation
            ? attribute.name
            : this.$t(`AUTOMATION.ATTRIBUTES.${attribute.name}`),
        };
      });
    },
  },
};
</script>

<template>
  <div>
    <woot-modal-header :header-title="$t('AUTOMATION.EDIT.TITLE')" />
    <div class="flex flex-col modal-content">
      <div v-if="automation" class="w-full">
        <woot-input
          v-model="automation.name"
          :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
          type="text"
          :class="{ error: errors.name }"
          :error="errors.name ? $t('AUTOMATION.ADD.FORM.NAME.ERROR') : ''"
          :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
        />
        <woot-input
          v-model="automation.description"
          :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
          type="text"
          :class="{ error: errors.description }"
          :error="
            errors.description ? $t('AUTOMATION.ADD.FORM.DESC.ERROR') : ''
          "
          :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
        />
        <div class="event_wrapper">
          <label :class="{ error: errors.event_name }">
            {{ $t('AUTOMATION.ADD.FORM.EVENT.LABEL') }}
            <select
              v-model="automation.event_name"
              @change="onEventChange(automation)"
            >
              <option
                v-for="event in automationRuleEvents"
                :key="event.key"
                :value="event.key"
              >
                {{ event.value }}
              </option>
            </select>
            <span v-if="errors.event_name" class="message">
              {{ $t('AUTOMATION.ADD.FORM.EVENT.ERROR') }}
            </span>
          </label>
        </div>
        <!-- // Execution Mode Toggle -->
        <div class="mb-6">
          <label class="block mb-2">
            {{ $t('AUTOMATION.BRANCHES.EXECUTION_MODE') }}
          </label>
          <div class="flex gap-4">
            <label class="flex items-center gap-2">
              <input
                v-model="automation.execution_mode"
                type="radio"
                value="simple"
              />
              <span>{{ $t('AUTOMATION.BRANCHES.MODE_SIMPLE') }}</span>
            </label>
            <!-- Oculto temporalmente - en desarrollo -->
            <!-- <label class="flex items-center gap-2">
              <input
                v-model="automation.execution_mode"
                type="radio"
                value="branched"
              />
              <span>{{ $t('AUTOMATION.BRANCHES.MODE_BRANCHED') }}</span>
            </label> -->
          </div>
        </div>
        <!-- // Branches Editor (branched mode) -->
        <BranchesEditor
          v-if="automation.execution_mode === 'branched'"
          :key="`branches-${automation.event_name}-${automation.execution_mode}`"
          v-model:branches="automation.branches"
          :filter-attributes="
            getTranslatedAttributes(automationTypes, automation.event_name)
          "
          :action-types="automationActionTypes"
          :get-condition-dropdown-values="getConditionDropdownValues"
          :get-action-dropdown-values="getActionDropdownValues"
          :all-custom-attributes="allCustomAttributes"
          :automation="automation"
          :automation-types="automationTypes"
        />
        <!-- // Conditions Start (simple mode) -->
        <template v-if="automation.execution_mode === 'simple'">
          <section>
            <label>
              {{ $t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL') }}
            </label>
            <div
              class="w-full p-4 mb-4 border border-solid rounded-lg bg-n-slate-2 dark:bg-n-solid-2 border-n-strong"
            >
              <FilterInputBox
                v-for="(condition, i) in automation.conditions"
                :key="i"
                v-model="automation.conditions[i]"
                :filter-attributes="
                  getTranslatedAttributes(
                    automationTypes,
                    automation.event_name
                  )
                "
                :input-type="
                  getInputType(
                    allCustomAttributes,
                    automationTypes,
                    automation,
                    automation.conditions[i].attribute_key
                  )
                "
                :operators="
                  getOperators(
                    allCustomAttributes,
                    automationTypes,
                    automation,
                    mode,
                    automation.conditions[i].attribute_key
                  )
                "
                :dropdown-values="
                  getConditionDropdownValues(
                    automation.conditions[i].attribute_key
                  )
                "
                :custom-attribute-type="
                  getCustomAttributeType(
                    automationTypes,
                    automation,
                    automation.conditions[i].attribute_key
                  )
                "
                :show-query-operator="i !== automation.conditions.length - 1"
                :error-message="
                  errors[`condition_${i}`]
                    ? $t(`AUTOMATION.ERRORS.${errors[`condition_${i}`]}`)
                    : ''
                "
                @reset-filter="resetFilter(i, automation.conditions[i])"
                @remove-filter="removeFilter(i)"
              />
              <div class="mt-4">
                <NextButton
                  icon="i-lucide-plus"
                  blue
                  faded
                  sm
                  :label="$t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL')"
                  @click="appendNewCondition"
                />
              </div>
            </div>
          </section>
          <!-- // Conditions End -->
          <!-- // Actions Start -->
          <section>
            <label>
              {{ $t('AUTOMATION.ADD.FORM.ACTIONS.LABEL') }}
            </label>
            <div
              class="w-full p-4 mb-4 border border-solid rounded-lg bg-n-slate-2 dark:bg-n-solid-2 border-n-strong"
            >
              <AutomationActionInput
                v-for="(action, i) in automation.actions"
                :key="i"
                v-model="automation.actions[i]"
                :action-types="automationActionTypes"
                :dropdown-values="getActionDropdownValues(action.action_name)"
                :show-action-input="
                  showActionInput(automationActionTypes, action.action_name)
                "
                :error-message="
                  errors[`action_${i}`]
                    ? $t(`AUTOMATION.ERRORS.${errors[`action_${i}`]}`)
                    : ''
                "
                :initial-file-name="getFileName(action, automation.files)"
                @reset-action="resetAction(i)"
                @remove-action="removeAction(i)"
              />
              <div class="mt-4">
                <NextButton
                  icon="i-lucide-plus"
                  blue
                  faded
                  sm
                  :label="$t('AUTOMATION.ADD.ACTION_BUTTON_LABEL')"
                  @click="appendNewAction"
                />
              </div>
            </div>
          </section>
          <!-- // Actions End -->
        </template>
        <div class="w-full">
          <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
            <NextButton
              faded
              slate
              type="reset"
              :label="$t('AUTOMATION.EDIT.CANCEL_BUTTON_TEXT')"
              @click.prevent="onClose"
            />
            <NextButton
              solid
              blue
              type="submit"
              :label="$t('AUTOMATION.EDIT.SUBMIT')"
              @click="emitSaveAutomation"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.event_wrapper {
  select {
    @apply m-0;
  }
  .info-message {
    @apply text-xs text-n-teal-10  text-right;
  }

  @apply mb-6;
}
</style>
